/**
 * GranularActionsRow — compact icon row of 10 per-node investigation actions.
 * Sits between the canvas counter and the tab bar in Node Inspector.
 * Each button shows a live status dot (idle/running/done/error/na).
 */
import React from 'react';
import { Tag, Shield, Building2, Zap, FileCode2, BarChart2, ArrowLeftToLine, ArrowRightFromLine, Users, Network, Banknote, ShieldCheck } from 'lucide-react';

export type GranularAction =
    | 'entity_lookup'
    | 'sanctions'
    | 'exchange_detection'
    | 'velocity'
    | 'smart_contract'
    | 'risk_score'
    | 'backward_trace'
    | 'forward_trace'
    | 'follow_money'
    | 'counterparty'
    | 'multi_hop'
    | 'compliant_exchange';

export type ActionStatus = 'idle' | 'running' | 'done' | 'error' | 'na';
export type ActionStatuses = Partial<Record<GranularAction, ActionStatus>>;

interface ActionDef {
    key: GranularAction;
    icon: React.ElementType;
    label: string;
    group: 'inspect' | 'trace';
    timing: string;
}

const ACTIONS: ActionDef[] = [
    { key: 'entity_lookup',       icon: Tag,              label: 'Entity Lookup',         group: 'inspect', timing: '2-5s'     },
    { key: 'sanctions',           icon: Shield,           label: 'Sanctions Screen',      group: 'inspect', timing: 'instant'  },
    { key: 'exchange_detection',  icon: Building2,        label: 'Exchange Detection',    group: 'inspect', timing: '5-15s'    },
    { key: 'velocity',            icon: Zap,              label: 'Velocity Profile',      group: 'inspect', timing: '3-10s'    },
    { key: 'smart_contract',      icon: FileCode2,        label: 'Smart Contract',        group: 'inspect', timing: '2-5s'     },
    { key: 'risk_score',          icon: BarChart2,        label: 'Risk Score',            group: 'inspect', timing: '3-8s'     },
    { key: 'backward_trace',      icon: ArrowLeftToLine,  label: 'Backward Trace',        group: 'trace',   timing: '20-180s'  },
    { key: 'forward_trace',       icon: ArrowRightFromLine, label: 'Forward Trace',       group: 'trace',   timing: '20-180s'  },
    { key: 'follow_money',        icon: Banknote,         label: 'Follow the Money',      group: 'trace',   timing: '20-180s'  },
    { key: 'counterparty',        icon: Users,            label: 'Counterparty Analysis', group: 'trace',   timing: '10-20s'   },
    { key: 'multi_hop',           icon: Network,          label: 'Multi-Hop Exposure',    group: 'trace',   timing: '30-120s'  },
    { key: 'compliant_exchange',  icon: ShieldCheck,      label: 'Find Compliant Exit',   group: 'trace',   timing: '10-60s'   },
];

const EVM_CHAINS = new Set(['ETH', 'BNB', 'OPBNB', 'MATIC', 'POLYGON', 'ARB', 'BASE', 'OP', 'AVAX', 'LINEA', 'SCROLL']);

const StatusDot: React.FC<{ status: ActionStatus }> = ({ status }) => {
    if (status === 'idle' || status === 'na') return null;
    if (status === 'running') return (
        <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
    );
    if (status === 'done') return (
        <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-500" />
    );
    // error
    return (
        <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />
    );
};

interface GranularActionsRowProps {
    chain: string | null;
    actionStatuses: ActionStatuses;
    onRunAction: (action: GranularAction) => void;
}

export const GranularActionsRow: React.FC<GranularActionsRowProps> = ({ chain, actionStatuses, onRunAction }) => {
    const isEvm = EVM_CHAINS.has((chain || '').toUpperCase());
    const inspectActions = ACTIONS.filter(a => a.group === 'inspect');
    const traceActions = ACTIONS.filter(a => a.group === 'trace');

    const renderButton = (action: ActionDef) => {
        const status = actionStatuses[action.key] ?? 'idle';
        const isNa = action.key === 'smart_contract' && !isEvm;
        const effectiveStatus: ActionStatus = isNa ? 'na' : status;
        const isDisabled = effectiveStatus === 'running' || effectiveStatus === 'na';

        const naTitle = action.key === 'smart_contract'
            ? 'Smart Contract check: EVM chains only (ETH, BNB, MATIC...)'
            : undefined;

        return (
            <button
                key={action.key}
                data-action={action.key}
                onClick={() => !isDisabled && onRunAction(action.key)}
                disabled={isDisabled}
                title={naTitle ?? `${action.label} (${action.timing})`}
                aria-label={naTitle ?? `${action.label} — takes ${action.timing}`}
                className={`relative p-1.5 rounded transition-colors
                    ${effectiveStatus === 'na'
                        ? 'opacity-25 cursor-not-allowed text-muted-foreground'
                        : effectiveStatus === 'done'
                            ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10'
                            : effectiveStatus === 'error'
                                ? 'text-red-500 hover:bg-red-500/10'
                                : effectiveStatus === 'running'
                                    ? 'text-primary cursor-wait'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
            >
                <action.icon className="w-3.5 h-3.5" />
                <StatusDot status={effectiveStatus} />
            </button>
        );
    };

    // 12 icons plus two group labels need ~460px; the panel floors at 440px and
    // loses 32px to padding, so this row used to clip its last Trace actions with
    // no way to reach them. Each labelled group is now an unbreakable unit and the
    // parent wraps between them — Trace drops to a second line instead of off the
    // edge. Same reason the tab strip wraps rather than scrolls.
    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1 mb-1 border-b border-border/50" data-tour="granular-actions">
            <div className="flex items-center gap-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/50 font-semibold shrink-0">Inspect</span>
                <div className="flex items-center gap-0.5">
                    {inspectActions.map(renderButton)}
                </div>
            </div>
            <div className="flex items-center gap-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/50 font-semibold shrink-0">Trace</span>
                <div className="flex items-center gap-0.5">
                    {traceActions.map(renderButton)}
                </div>
            </div>
        </div>
    );
};
